import React, { useState, useEffect } from 'react';
import { InputMask } from '@react-input/mask';
import CurrencyInput from "react-currency-input-field";
import Select from "react-select";
import api from '../../../../config/axios';
import Swal from 'sweetalert2';
import moment from 'moment';
import { autoCompleteClientes, autoCompleteServicos, autoCompleteProdutos, autoCompleteColaboradoresComanda } from "../../../../components/utils/autocompletes";
import { FaTimes, FaUserPlus } from 'react-icons/fa';
import EstornosItemModal from './EstornosItem';

const ComandaFechadaModal = ({ comanda, onClose, onComandaAtualizada }) => {
    const [activeTab, setActiveTab] = useState('itens');
    const [caixas, setCaixas] = useState([]);
    const [maquinasList, setMaquinasList] = useState([]);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [showEstornosModal, setShowEstornosModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [contasBancarias, setContasBancarias] = useState([]);
    const [bandeirasCartao, setBandeirasCartao] = useState({});
    const { inputPesquisaCliente, clientesOptions, handleAutoCompleteClientes } = autoCompleteClientes();
    const { inputPesquisaColaborador, colaboradoresOptions, handleAutoCompleteColaboradores } = autoCompleteColaboradoresComanda();
    const { inputPesquisaServico, servicosOptions, handleAutoCompleteServicos } = autoCompleteServicos();
    const { inputPesquisaProduto, produtosOptions, handleAutoCompleteProdutos } = autoCompleteProdutos();

    const [formData, setFormData] = useState({
        id: '',
        cliente: '',
        data_abertura: '',
        data_finalizada: '',
        status: 'finalizada',
        observacao: '',
        caixa_responsavel: '',
        itens: [],
        pagamentos: []
    });

    const customStyles = {
        menu: (provided) => ({
            ...provided,
            whiteSpace: "pre-line",
        }),
        option: (provided) => ({
            ...provided,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
        }),
    };

    useEffect(() => {
        const carregarDados = async () => {
            if (comanda != null) {
                setFormData({
                    ...comanda,
                });
                obterCaixas(comanda);

                if (comanda.pagamentos && comanda.pagamentos.length > 0) {
                    for (let i = 0; i < comanda.pagamentos.length; i++) {
                        const pagamento = comanda.pagamentos[i];
                        if ((pagamento.tipo_pagamento === 'pagamento_credito' || pagamento.tipo_pagamento === 'pagamento_debito') && pagamento.conta_bancaria_id) {
                            const bandeiras = await fetchBandeirasCartao(pagamento.conta_bancaria_id, pagamento.tipo_pagamento);
                            setBandeirasCartao(prevState => ({
                                ...prevState,
                                [pagamento.id]: bandeiras
                            }));
                        }
                    }
                }
            }
            await fetchMaquinas();
            await fetchContasBancarias();
        };

        carregarDados();
    }, [comanda]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const obterCaixas = async (comanda) => {
        const response = await api.get(`/caixas/${comanda.caixa_responsavel}`);
        setCaixas([response.data]);
    }

    const fetchMaquinas = async () => {
        try {
            Swal.fire({
                title: "Carregando...",
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await api.get('/maquinas-pagamento?page=1&limit=200');
            setMaquinasList(response.data.records || []);
            Swal.close();
        } catch (error) {
            Swal.close();
        }
    };

    const fetchContasBancarias = async () => {
        try {
            const response = await api.get('/contas-bancarias/para-lancamentos');
            setContasBancarias(response.data || []);
        } catch (error) {
        }
    };

    const fetchBandeirasCartao = async (contaBancariaId, tipo) => {
        try {
            if (tipo === 'pagamento_credito') {
                const responseAvista = await api.get(`/cartoes?conta_bancaria_id=${contaBancariaId}&tipo=credito_avista&limit=1000`);
                const responseParcelado = await api.get(`/cartoes?conta_bancaria_id=${contaBancariaId}&tipo=credito_parcelado&limit=1000`);

                const bandeirasAvista = (responseAvista.data.records || []).map(b => ({ ...b, tipo_exibicao: 'À Vista' }));
                const bandeirasParcelado = (responseParcelado.data.records || []).map(b => ({ ...b, tipo_exibicao: 'Parcelado' }));

                return [...bandeirasAvista, ...bandeirasParcelado];
            } else {
                const response = await api.get(`/cartoes?conta_bancaria_id=${contaBancariaId}&tipo=debito&limit=1000`);
                return response.data.records || [];
            }
        } catch (error) {
            return [];
        }
    };

    const calcularTotal = (itens) => {
        let total = 0;
        for (let i = 0; i < itens.length; i++) {
            total += parseFloat(itens[i].final_item);
        }
        return total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const calcularTroco = () => {
        try {
            const totalPagamentos = formData.pagamentos.reduce((acc, pagamento) => {
                if (!pagamento.valor) return acc;

                const valorLimpo = pagamento.valor.toString()
                    .replace('R$', '')
                    .trim();

                const valorNumerico = valorLimpo.includes(',')
                    ? valorLimpo.replace(/\./g, '').replace(',', '.')
                    : valorLimpo;

                const valor = parseFloat(valorNumerico);
                return isNaN(valor) ? acc : acc + valor;
            }, 0);

            const totalComanda = formData.itens.reduce((acc, item) => {
                if (!item.final_item) return acc;
                return acc + parseFloat(item.final_item);
            }, 0);

            return totalPagamentos - totalComanda;
        } catch (error) {
            return 0;
        }
    };

    const formatarMoeda = (valor) => {
        return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const cancelarComanda = async () => {
        if (!formData.observacao || !formData.observacao.trim()) {
            Swal.fire('Atenção', 'Por favor, preencha o campo de observações com o motivo do cancelamento antes de cancelar a comanda.', 'warning');
            return;
        }

        const result = await Swal.fire({
            title: 'Cancelar Comanda',
            text: `Deseja cancelar a comanda #${formData.id}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, cancelar',
            cancelButtonText: 'Não'
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: "Carregando...",
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const response = await api.put(`/comandas/${formData.id}/cancelar`, {
                    observacao: formData.observacao
                });

                if (response.data.success) {
                    await Swal.fire('Sucesso!', 'Comanda cancelada com sucesso.', 'success');
                    onComandaAtualizada();
                    onClose();
                } else {
                    Swal.fire('Erro', response.data.message || 'Erro ao cancelar a comanda.', 'error');
                }
            } catch (error) {
                Swal.close();
                Swal.fire('Erro', error.response.data.message || 'Erro ao cancelar a comanda.', 'error');
            }
        }
    };

    const abrirEstornos = (e, item) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedItem(item);
        setShowEstornosModal(true);
    };

    const fecharEstornos = () => {
        setShowEstornosModal(false);
        setSelectedItem(null);
    };

    const handleEstornoRealizado = () => {
        if (onComandaAtualizada) {
            onComandaAtualizada();
        }
        onClose();
    };

    return (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered" style={{
                maxWidth: windowWidth < 1128 ? '90%' : '70%',
                margin: '0.5rem auto'
            }}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h4 className="fw-bold mt-2">Comanda Finalizada</h4>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body">
                        <form>
                            <ul className="nav nav-tabs mt-3">
                                <li className="nav-item">
                                    <a
                                        className={activeTab === 'itens' ? 'nav-link active' : 'nav-link'}
                                        aria-current="page"
                                        onClick={() => setActiveTab('itens')}>
                                        Itens</a>
                                </li>
                                <li className="nav-item">
                                    <a
                                        className={activeTab === 'pagamento' ? 'nav-link active' : 'nav-link'}
                                        aria-current="page"
                                        onClick={() => setActiveTab('pagamento')}>
                                        Pagamento</a>
                                </li>
                                <li className="nav-item">
                                    <a
                                        className={activeTab === 'informacoes' ? 'nav-link active' : 'nav-link'}
                                        aria-current="page"
                                        onClick={() => setActiveTab('informacoes')}>
                                        Informações</a>
                                </li>
                            </ul>

                            {activeTab === 'itens' && (
                                <div>
                                    <div className="row mt-3">
                                        <div className="col-lg-6 col-12">
                                            <label className="form-label fw-bold">Cliente</label>
                                            <Select
                                                options={clientesOptions}
                                                styles={customStyles}
                                                placeholder="Cliente"
                                                isDisabled={true}
                                                value={formData.cliente}
                                            />
                                        </div>
                                        <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Data da Comanda</label>
                                            <InputMask
                                                mask="__/__/____ __:__:__"
                                                replacement={{ _: /\d/ }}
                                                name="data_abertura"
                                                className="form-control"
                                                autoComplete="off"
                                                disabled={true}
                                                value={formData.data_abertura}
                                                placeholder="DD/MM/AAAA"
                                            />
                                        </div>
                                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">N°</label>
                                            <input
                                                type="text"
                                                name="id"
                                                className="form-control"
                                                autoComplete="off"
                                                disabled
                                                value={formData.id}
                                            />
                                        </div>
                                    </div>
                                    <div className="row mt-5">
                                        <div className="col-12">
                                            <div className="table-responsive max-h-400" style={{ maxHeight: '45vh' }}>
                                                <table className="table">
                                                    <thead className="table-custom-primary">
                                                        <tr>
                                                            <th>Item</th>
                                                            <th>Profissional</th>
                                                            <th>Qtd.</th>
                                                            <th className="text-nowrap">Valor (R$)</th>
                                                            <th className="text-nowrap">Desc. (%)</th>
                                                            <th className="text-nowrap">Final (R$)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formData.itens.map((item) => (
                                                            <tr key={item.id}>
                                                                <td className="text-nowrap">
                                                                    <div style={{ width: '400px' }}>
                                                                        {item.tipo_item === "servico" ? (
                                                                            <Select
                                                                                options={servicosOptions}
                                                                                styles={customStyles}
                                                                                placeholder="Serviço"
                                                                                isDisabled={true}
                                                                                value={item.servico}
                                                                            />
                                                                        ) : item.tipo_item === "produto" ? (
                                                                            <Select
                                                                                options={produtosOptions}
                                                                                styles={customStyles}
                                                                                placeholder="Produto"
                                                                                isDisabled={true}
                                                                                value={item.produto}
                                                                            />
                                                                        ) : (
                                                                            <span className="form-control-plaintext fw-bold">Caixinha</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <Select
                                                                        options={colaboradoresOptions}
                                                                        styles={customStyles}
                                                                        placeholder=""
                                                                        isDisabled={true}
                                                                        value={item.profissional}
                                                                    />
                                                                    {item.tipo_item === 'servico' && item.assistentes && item.assistentes.length > 0 && (
                                                                        <div className="mt-2">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-link btn-sm p-0 text-decoration-none"
                                                                                disabled
                                                                            >
                                                                                <FaUserPlus className="me-1" />
                                                                                Assistentes:
                                                                            </button>
                                                                            {item.assistentes.map((assistente, index) => (
                                                                                <div key={index}>
                                                                                    <small className="text-muted">{assistente.profissional?.label || 'Assistente'}</small>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        name="qtd"
                                                                        className="form-control"
                                                                        autoComplete="off"
                                                                        value={item.qtd}
                                                                        style={{ width: '80px' }}
                                                                        disabled={true}
                                                                    />
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <CurrencyInput
                                                                        name="valor_item"
                                                                        placeholder="R$ 0,00"
                                                                        value={item.valor_item}
                                                                        decimalsLimit={2}
                                                                        decimalSeparator=","
                                                                        groupSeparator="."
                                                                        prefix="R$ "
                                                                        className="form-control"
                                                                        disabled={true}
                                                                        autoComplete="off"
                                                                        style={{ width: '150px' }}
                                                                    />
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <input
                                                                        className="form-control"
                                                                        name="desconto_item"
                                                                        autoComplete="off"
                                                                        disabled={true}
                                                                        value={item.desconto_item}
                                                                        placeholder="0%"
                                                                    />
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    {item.final_item != ''
                                                                        ? parseFloat(item.final_item).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                                                        : 'R$ 0,00'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'pagamento' && (
                                <div>
                                    <div className="row mt-3 align-items-end">
                                        <div className="col-lg-4 col-12">
                                            <label className="form-label fw-bold">Caixa Responsável</label>
                                            <select className="form-select"
                                                value={formData.caixa_responsavel}
                                                disabled={true}>
                                                <option value="" hidden>Selecione...</option>
                                                {caixas.map((caixa) => (
                                                    <option key={caixa.id} value={caixa.id}>{caixa.colaborador.nome} - {moment(caixa.data_abertura).format('DD/MM/YYYY')}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Saldo do Cliente</label>
                                            <h4 className="form-label fw-bold">R$ 0,00</h4>
                                        </div>
                                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Total da Comanda</label>
                                            <h4 className="form-label fw-bold">{calcularTotal(formData.itens)}</h4>
                                        </div>
                                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Total a Cobrar</label>
                                            <h4 className="form-label fw-bold text-success">{calcularTotal(formData.itens)}</h4>
                                        </div>
                                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Troco</label>
                                            <h4 className="form-label fw-bold" style={{
                                                color: calcularTroco() >= 0 ? '#198754' : '#dc3545'
                                            }}>
                                                {formatarMoeda(calcularTroco())}
                                            </h4>
                                        </div>
                                    </div>
                                    <div className="row mt-5">
                                        <div className="col-12">
                                            <div className="table-responsive max-h-400" style={{ maxHeight: '45vh' }}>
                                                <table className="table">
                                                    <thead className="table-custom-primary">
                                                        <tr>
                                                            <th className="text-nowrap">Forma de Pagamento</th>
                                                            <th>Informações</th>
                                                            <th className="text-nowrap">Conta Bancária</th>
                                                            <th className="text-nowrap">Bandeira</th>
                                                            <th className="text-nowrap">Máquina</th>
                                                            <th className="text-nowrap">Valor (R$)</th>
                                                            <th className="text-nowrap">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formData.pagamentos.map((item) => (
                                                            <tr key={item.id}>
                                                                <td className="text-nowrap">
                                                                    <select className="form-select"
                                                                        value={item.tipo_pagamento}
                                                                        style={{ width: '200px' }}
                                                                        disabled={true}>
                                                                        <option value="pagamento_dinheiro">Dinheiro</option>
                                                                        <option value="pagamento_credito">Cartão Crédito</option>
                                                                        <option value="pagamento_debito">Cartão Débito</option>
                                                                        <option value="pagamento_pix">Pix</option>
                                                                        <option value="pagamento_boleto">Boleto</option>
                                                                        <option value="pagamento_ted">TED</option>
                                                                        <option value="pagamento_doc">DOC</option>
                                                                        <option value="uso_cortesia">Cortesia</option>
                                                                    </select>
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <input
                                                                        type="text"
                                                                        name="informacoes"
                                                                        className="form-control"
                                                                        autoComplete="off"
                                                                        value={item.informacoes}
                                                                        disabled={true}
                                                                        style={{ width: '250px' }}
                                                                    />
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    {(item.tipo_pagamento === 'pagamento_pix' || item.tipo_pagamento === 'pagamento_credito' || item.tipo_pagamento === 'pagamento_debito') ? (
                                                                        <select
                                                                            className="form-select"
                                                                            style={{ width: '200px' }}
                                                                            value={item.conta_bancaria_id || ''}
                                                                            disabled={true}
                                                                        >
                                                                            <option value="">Selecione...</option>
                                                                            {contasBancarias.map(conta => (
                                                                                <option key={conta.id} value={conta.id}>
                                                                                    {conta.nome_exibicao || conta.nome}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <div style={{ width: '200px' }}>-</div>
                                                                    )}
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    {(item.tipo_pagamento === 'pagamento_credito' || item.tipo_pagamento === 'pagamento_debito') && item.conta_bancaria_id ? (
                                                                        <select
                                                                            className="form-select"
                                                                            style={{ width: '200px' }}
                                                                            value={item.bandeira_id || ''}
                                                                            disabled={true}
                                                                        >
                                                                            <option value="">Selecione...</option>
                                                                            {(bandeirasCartao[item.id] || []).map(bandeira => (
                                                                                <option key={bandeira.id} value={bandeira.id}>
                                                                                    {bandeira.bandeira}{bandeira.tipo_exibicao ? ` - ${bandeira.tipo_exibicao}` : ''}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <div style={{ width: '200px' }}>-</div>
                                                                    )}
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    {(item.tipo_pagamento === 'pagamento_credito' || item.tipo_pagamento === 'pagamento_debito') ? (
                                                                        <select
                                                                            className="form-select"
                                                                            style={{ width: '250px' }}
                                                                            value={item.numero_serial}
                                                                            disabled={true}
                                                                        >
                                                                            <option value="" hidden>Selecione...</option>
                                                                            {maquinasList.map(maquina => (
                                                                                <option key={maquina.id} value={maquina.serial}>
                                                                                    {maquina.apelido} - {maquina.serial}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <div style={{ width: '250px' }}>-</div>
                                                                    )}
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <CurrencyInput
                                                                        name="valor"
                                                                        placeholder="R$ 0,00"
                                                                        value={item.valor}
                                                                        decimalsLimit={2}
                                                                        decimalSeparator=","
                                                                        groupSeparator="."
                                                                        prefix="R$ "
                                                                        className="form-control"
                                                                        autoComplete="off"
                                                                        style={{ width: '150px' }}
                                                                        disabled={true}
                                                                    />
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <span className={`badge ${item.status_pagamento === 'aprovado' ? 'bg-success' :
                                                                        item.status_pagamento === 'erro' ? 'bg-danger' :
                                                                            'bg-warning'
                                                                        }`} style={{ fontSize: '0.9rem', padding: '8px' }}>
                                                                        {item.status_pagamento === 'aprovado' ? 'Aprovado' :
                                                                            item.status_pagamento === 'erro' ? 'Erro' :
                                                                                'Pendente'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'informacoes' && (
                                <div>
                                    <div className="row mt-3">
                                        <div className="col-md-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Observações</label>
                                            <textarea
                                                name="observacao"
                                                className="form-control"
                                                autoComplete="off"
                                                rows="4"
                                                value={formData.observacao}
                                                onChange={(e) => setFormData({ ...formData, observacao: e.target.value })}
                                            ></textarea>
                                        </div>
                                    </div>
                                    <div className="row mt-3">
                                        <div className="col-lg-6 col-12">
                                            {comanda != null && comanda?.criado_por != null && (
                                                <>
                                                    <label className="form-label fw-bold">Criado por: </label>
                                                    <span className="form-label"> {comanda.criado_por}</span>
                                                    <br />
                                                    <label className="form-label fw-bold">Criado em: </label>
                                                    <span className="form-label"> {moment(comanda.criado_em).format('DD/MM/YYYY HH:mm:ss')}</span>
                                                </>
                                            )}
                                        </div>
                                        <div className="col-lg-6 col-12">
                                            {comanda != null && comanda?.alterado_por != null && (
                                                <>
                                                    <label className="form-label fw-bold">Atualizado por: </label>
                                                    <span className="form-label"> {comanda.alterado_por}</span>
                                                    <br />
                                                    <label className="form-label fw-bold">Atualizado em: </label>
                                                    <span className="form-label"> {moment(comanda.alterado_em).format('DD/MM/YYYY HH:mm:ss')}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>
                    <div className="modal-footer mt-4">
                        <div className="row">
                            <div className="col-12 text-end">
                                <button className="btn btn-outline-secondary me-3" type="button" onClick={cancelarComanda}>
                                    <FaTimes /> Cancelar Comanda
                                </button>
                                <button className="btn btn-outline-danger" type="button" onClick={onClose}>
                                    <FaTimes /> Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showEstornosModal && selectedItem && (
                <EstornosItemModal
                    item={selectedItem}
                    onClose={fecharEstornos}
                    onEstornoRealizado={handleEstornoRealizado}
                />
            )}
        </div>
    );
};

export default ComandaFechadaModal;
